/**
 * yAggregation.js — pure SQL rewriter used by the Chart Editor's "Y
 * aggregation" toolbar.
 *
 * Naive subquery wrapping (`SELECT ... FROM (<inner>) AS chart_sub`) breaks in
 * two ways:
 *   1. A trailing `LIMIT N` from the preview lives *inside* the subquery, so
 *      the aggregate runs against only N raw rows instead of the full set.
 *   2. The inner projection still carries every original column even though
 *      the outer query only references one of them.
 *
 * Instead we rewrite the *outer* SELECT in place: replace its projection list
 * with the aggregate, append a GROUP BY, and drop the result-shaping clauses
 * (LIMIT/OFFSET/ORDER BY/HAVING/QUALIFY/WINDOW/FETCH) that the user wrote for
 * the un-aggregated preview. WITH ... CTEs and WHERE filters are preserved.
 *
 * For SQL we cannot safely rewrite (compound UNION/INTERSECT/EXCEPT, or where
 * SELECT/FROM cannot be located at depth 0) we fall back to subquery wrapping
 * with the inner LIMIT/OFFSET stripped so the aggregate at least sees all
 * rows.
 *
 * Exported as CommonJS so the module is callable both from the React app
 * (via Webpack interop) and from Playwright unit tests running in raw Node.
 */

'use strict';

const Y_AGG_TERMINATORS = new Set([
  'GROUP BY', 'HAVING', 'QUALIFY', 'WINDOW',
  'ORDER BY', 'LIMIT', 'OFFSET', 'FETCH',
]);

const Y_AGG_SET_OPS = new Set(['UNION', 'INTERSECT', 'EXCEPT', 'MINUS']);

/**
 * Scan `sql` and return all SQL keywords that occur at paren depth 0 (i.e.
 * outside any subquery), respecting string literals, quoted identifiers, and
 * line/block comments. Two-word keywords (GROUP BY, ORDER BY) are recognized.
 */
function findTopLevelClauses(sql) {
  const s = String(sql);
  const len = s.length;
  const clauses = [];
  let hasSetOp = false;
  let depth = 0;
  let i = 0;

  while (i < len) {
    const c = s[i];

    // String / quoted-identifier literals — skip until matching quote,
    // honoring doubled-quote escapes (`''`, `""`).
    if (c === "'" || c === '"' || c === '`') {
      const q = c;
      i += 1;
      while (i < len) {
        if (s[i] === q) {
          if (s[i + 1] === q) { i += 2; continue; }
          i += 1;
          break;
        }
        i += 1;
      }
      continue;
    }

    // Line comment
    if (c === '-' && s[i + 1] === '-') {
      while (i < len && s[i] !== '\n') i += 1;
      continue;
    }
    // Block comment
    if (c === '/' && s[i + 1] === '*') {
      i += 2;
      while (i < len && !(s[i] === '*' && s[i + 1] === '/')) i += 1;
      i += 2;
      continue;
    }

    if (c === '(') { depth += 1; i += 1; continue; }
    if (c === ')') { depth -= 1; i += 1; continue; }

    if (depth === 0 && /[A-Za-z_]/.test(c)) {
      let j = i;
      while (j < len && /[A-Za-z0-9_]/.test(s[j])) j += 1;
      const word = s.slice(i, j).toUpperCase();

      const peekSecond = (expected) => {
        let k = j;
        while (k < len && /\s/.test(s[k])) k += 1;
        let m = k;
        while (m < len && /[A-Za-z0-9_]/.test(s[m])) m += 1;
        return s.slice(k, m).toUpperCase() === expected ? m : -1;
      };

      let endPos = j;
      let kw = null;
      if (word === 'SELECT' || word === 'FROM' || word === 'WHERE'
        || word === 'HAVING' || word === 'QUALIFY' || word === 'WINDOW'
        || word === 'LIMIT'  || word === 'OFFSET'  || word === 'FETCH') {
        kw = word;
      } else if (word === 'GROUP') {
        const e = peekSecond('BY');
        if (e > 0) { kw = 'GROUP BY'; endPos = e; }
      } else if (word === 'ORDER') {
        const e = peekSecond('BY');
        if (e > 0) { kw = 'ORDER BY'; endPos = e; }
      } else if (Y_AGG_SET_OPS.has(word)) {
        hasSetOp = true;
      }

      if (kw) clauses.push({ kw, start: i, end: endPos });
      i = j;
      continue;
    }

    i += 1;
  }

  return { clauses, hasSetOp };
}

const Y_AGG_QUOTE = (c) => `"${String(c).replace(/"/g, '""')}"`;

// Time-bucket units accepted by the rewriter. Values map directly to
// Snowflake's DATE_TRUNC argument (case-insensitive there, but we emit
// uppercase for readability).
const Y_AGG_TIME_UNITS = new Set([
  'YEAR', 'QUARTER', 'MONTH', 'WEEK', 'DAY', 'HOUR', 'MINUTE',
]);

/**
 * Normalize a groupBy entry into the canonical `{ column, timeUnit }` shape.
 * Accepts either a bare string column name or an object with optional
 * `timeUnit`. Returns `null` for falsy / blank inputs. Unknown timeUnits are
 * coerced to undefined so downstream emission remains safe.
 */
function normalizeGroupEntry(raw) {
  if (raw == null) return null;
  if (typeof raw === 'string') {
    const c = raw.trim();
    return c ? { column: c, timeUnit: undefined } : null;
  }
  if (typeof raw === 'object' && raw.column) {
    const c = String(raw.column).trim();
    if (!c) return null;
    const u = raw.timeUnit ? String(raw.timeUnit).trim().toUpperCase() : '';
    return {
      column: c,
      timeUnit: u && u !== 'NONE' && Y_AGG_TIME_UNITS.has(u) ? u : undefined,
    };
  }
  return null;
}

/**
 * Normalize an identifier for case-insensitive comparison: trim and uppercase.
 * SQL identifiers (when unquoted) are case-insensitive in Snowflake, so
 * `"DATE"` and `"Date"` should be treated as the same column for the purposes
 * of GROUP BY deduplication.
 */
function normalizeIdent(c) {
  return String(c == null ? '' : c).trim().toUpperCase();
}

/**
 * Filter falsy entries, drop any column equal (case-insensitively) to `yCol`,
 * and dedupe by column name (case-insensitively, preserving first-seen
 * order). Returns an array of canonical `{ column, timeUnit }` objects.
 */
function dedupeGroupCols(groupBy, yCol) {
  const yKey = normalizeIdent(yCol);
  const seen = new Set();
  const out = [];
  for (const raw of groupBy || []) {
    const entry = normalizeGroupEntry(raw);
    if (!entry) continue;
    const key = normalizeIdent(entry.column);
    if (!key || key === yKey || seen.has(key)) continue;
    seen.add(key);
    out.push(entry);
  }
  return out;
}

/**
 * SQL expression for a normalized group entry. Wraps the column in
 * DATE_TRUNC when a timeUnit is set so callers get a date-bucketed value.
 */
function groupExprSql(entry) {
  const q = Y_AGG_QUOTE(entry.column);
  return entry.timeUnit ? `DATE_TRUNC('${entry.timeUnit}', ${q})` : q;
}

/**
 * Projection fragment for a group entry: bucketed expressions get aliased
 * back to the original column name so downstream consumers (Plotly traces,
 * etc.) keep referring to the same column.
 */
function groupProjectionSql(entry) {
  return entry.timeUnit
    ? `${groupExprSql(entry)} AS ${Y_AGG_QUOTE(entry.column)}`
    : Y_AGG_QUOTE(entry.column);
}

function buildAggExpr(yCol, func) {
  const q = Y_AGG_QUOTE(yCol);
  switch (func) {
    case 'sum':            return `SUM(${q})`;
    case 'avg':            return `AVG(${q})`;
    case 'min':            return `MIN(${q})`;
    case 'max':            return `MAX(${q})`;
    case 'count':          return `COUNT(${q})`;
    case 'count_distinct': return `COUNT(DISTINCT ${q})`;
    default:               return null;
  }
}

/**
 * Strip trailing `LIMIT N`/`OFFSET N`/`FETCH ...` clauses from a single
 * top-level SELECT so it can safely be wrapped as a subquery for aggregation.
 * Leaves WITH/WHERE/ORDER BY etc. untouched.
 */
function stripTrailingRowLimits(sql) {
  const cleaned = String(sql || '').trim().replace(/;\s*$/, '');
  const { clauses, hasSetOp } = findTopLevelClauses(cleaned);
  if (hasSetOp) return cleaned;
  const cutoff = clauses
    .filter((c) => c.kw === 'LIMIT' || c.kw === 'OFFSET' || c.kw === 'FETCH')
    .reduce((min, c) => (min < 0 || c.start < min ? c.start : min), -1);
  return cutoff < 0 ? cleaned : cleaned.slice(0, cutoff).trimEnd();
}

/**
 * Rewrite the outer SELECT of `sql` to project `<groupCols...>, <AGG>(<yCol>)`
 * with a GROUP BY clause. Returns the rewritten SQL on success, or `null` if
 * the query shape is not safe to rewrite (e.g. compound queries) so callers
 * can fall back to subquery wrapping.
 */
function rewriteOuterSelectForAggregation(sql, yCol, func, groupBy) {
  const cleaned = String(sql || '').trim().replace(/;\s*$/, '');
  if (!cleaned) return null;

  const { clauses, hasSetOp } = findTopLevelClauses(cleaned);
  if (hasSetOp) return null;

  // Defense in depth: refuse to rewrite anything that isn't a SELECT or a
  // WITH ... SELECT. Without this an `INSERT INTO t SELECT ...` would have
  // its inner SELECT rewritten and produce semantically different SQL.
  const head = cleaned.replace(/^(?:\s|--[^\n]*\n|\/\*[\s\S]*?\*\/)+/, '')
    .slice(0, 6).toUpperCase();
  if (!head.startsWith('SELECT') && !head.startsWith('WITH')) return null;

  const sel = clauses.find((c) => c.kw === 'SELECT');
  if (!sel) return null;
  const fr = clauses.find((c) => c.kw === 'FROM' && c.start > sel.end);
  if (!fr) return null;

  const aggExpr = buildAggExpr(yCol, func);
  if (!aggExpr) return null;

  const groupCols = dedupeGroupCols(groupBy, yCol);
  const newProjection = [
    ...groupCols.map(groupProjectionSql),
    `${aggExpr} AS ${Y_AGG_QUOTE(yCol)}`,
  ].join(', ');

  // Everything before SELECT (e.g. a WITH ... CTE block) is preserved as-is.
  const prefix = cleaned.slice(0, sel.end);
  // First clause after FROM that we want to drop or replace; WHERE is *not*
  // a terminator so user filters survive.
  const term = clauses.find((c) => Y_AGG_TERMINATORS.has(c.kw) && c.start > fr.end);
  const fromKeep = term
    ? cleaned.slice(fr.start, term.start)
    : cleaned.slice(fr.start);

  const groupClause = groupCols.length
    ? `\nGROUP BY ${groupCols.map(groupExprSql).join(', ')}`
    : '';
  // Time-bucketed groupings must produce a monotonic X axis for line/bar
  // charts; Snowflake doesn't guarantee row order without ORDER BY, so we
  // append one over every bucketed expression (NULLS first keeps gaps at
  // the leading edge instead of breaking the line in the middle).
  const orderClause = buildBucketOrderBy(groupCols);

  return `${prefix}\n  ${newProjection}\n${fromKeep.trimEnd()}${groupClause}${orderClause}`;
}

/**
 * Build an `ORDER BY` clause for groupBy entries that carry a timeUnit. We
 * sort by the DATE_TRUNC expression (not the alias) since the alias
 * `"DATE"` could collide with another projected column in the inner SQL
 * when bucketed expressions are aliased back to the source name.
 */
function buildBucketOrderBy(groupCols) {
  const bucketed = (groupCols || []).filter((e) => e && e.timeUnit);
  if (!bucketed.length) return '';
  return `\nORDER BY ${bucketed.map(groupExprSql).join(', ')} NULLS FIRST`;
}

/**
 * Apply a Y-axis aggregation to `inner`. Prefers in-place rewriting of the
 * outer SELECT; falls back to subquery wrapping (with inner LIMIT/OFFSET
 * stripped) when the SQL shape isn't safe to rewrite. Returns `inner`
 * unchanged when the inputs are incomplete.
 */
function wrapWithYAggregation(inner, yCol, func, groupBy) {
  if (!inner || !String(inner).trim() || !yCol || !func || func === 'none') {
    return inner;
  }
  const aggExpr = buildAggExpr(yCol, func);
  if (!aggExpr) return inner;

  const rewritten = rewriteOuterSelectForAggregation(inner, yCol, func, groupBy);
  if (rewritten) return rewritten;

  // Fallback: wrap as subquery, but strip trailing row-limit clauses so the
  // aggregate sees all rows, not just the preview slice.
  const safeInner = stripTrailingRowLimits(inner);
  const groupCols = dedupeGroupCols(groupBy, yCol);
  const selectCols = [
    ...groupCols.map(groupProjectionSql),
    `${aggExpr} AS ${Y_AGG_QUOTE(yCol)}`,
  ].join(', ');
  const groupClause = groupCols.length
    ? ` GROUP BY ${groupCols.map(groupExprSql).join(', ')}`
    : '';
  const orderClause = buildBucketOrderBy(groupCols);
  return `SELECT ${selectCols} FROM (\n${safeInner}\n) AS chart_sub${groupClause}${orderClause}`;
}

module.exports = {
  wrapWithYAggregation,
  rewriteOuterSelectForAggregation,
  stripTrailingRowLimits,
  findTopLevelClauses,
  dedupeGroupCols,
  normalizeIdent,
  normalizeGroupEntry,
  groupExprSql,
  groupProjectionSql,
  buildAggExpr,
  buildBucketOrderBy,
  Y_AGG_TIME_UNITS,
};
